import "../drawer/DocumentsDrawer.scss";
import "react-tooltip/dist/react-tooltip.css";

import debounce from "lodash/debounce";
import Table from "components/Table/Table";
import { PAGE_SIZE } from "constants/TableConstants";
import { FEATURE_FLAGS } from "constants/FeatureFlags";
import { useEffect, useMemo, useState } from "react";
import { Tooltip } from "react-tooltip";
import type { Metadoc } from "services/metadoc-api";
import type { RootState } from "services/store";
import { useSelector } from "utils/redux";
import { useMetaDocColumns } from "./metaDocColumns";

import type { ChangeEvent } from "react";

/**
 * props for rendering metadocs and optional parent-managed expansion state.
 *
 * `fileServices` is supplied by the same parent that usually owns expansion state,
 * while `onToggleExpansion` mutates that state and `expandedIds` reflects it back.
 */
type MetaDocTableProps = {
  documents: Array<Metadoc & { isChild?: boolean; parentMetadocId?: number }>;
  onDelete: (doc: Metadoc) => void;
  totalCount?: number;
  page?: number;
  onPageChange?: (page: number) => void;
  isLoading?: boolean;
  isDrawer?: boolean;
  /** available file-level services passed from the parent context. */
  fileServices?: Array<{
    id: number;
    type: string;
    type_verbose: string;
    label: string;
    msa?: number | null;
    costprojection?: number | null;
    lien_file?: number | null;
    document_management_file?: number | null;
    amended_review_screening?: number | null;
    workspace_url?: string | null;
    liens?: Array<{
      id: number;
      case_number: string;
      part_type: string;
      part_type_verbose: string;
    }>;
  }>;
  /** toggles a parent metadoc id in the parent-owned expansion set. */
  onToggleExpansion?: (metadocId: number) => void;
  /** current expanded metadoc ids owned by the parent and read by the columns. */
  expandedIds?: Set<number>;
  fileId?: number;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  emptyTableMessage?: string;
};

/**
 * MetaDocTable - table for metadocs
 *
 * shows metadocs with ability to expand and view source documents.
 * source documents shown as children are read-only.
 */
const MetaDocTable = ({
  documents,
  onDelete,
  totalCount = 0,
  page = 1,
  onPageChange,
  isLoading = false,
  isDrawer = false,
  onToggleExpansion,
  expandedIds,
  fileId,
  searchValue = "",
  onSearchChange,
  searchPlaceholder = "Search Meta Documents",
  emptyTableMessage = "No Meta Documents found",
}: MetaDocTableProps) => {
  const constants = useSelector((state: RootState) => state.shared.constants);
  const isMetadocCloningEnabled =
    constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.METADOC_CLONING_ENABLED];
  const columns = useMetaDocColumns({
    onDelete,
    onToggleExpansion,
    expandedIds,
    fileId,
    isMetadocCloningEnabled,
  });
  const [inputValue, setInputValue] = useState(searchValue);
  const debouncedOnSearchChange = useMemo(
    () =>
      debounce((value: string) => {
        onSearchChange?.(value);
      }, 300),
    [onSearchChange],
  );

  useEffect(() => {
    setInputValue(searchValue);
  }, [searchValue]);

  useEffect(() => {
    return () => {
      debouncedOnSearchChange.cancel();
    };
  }, [debouncedOnSearchChange]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setInputValue(nextValue);
    debouncedOnSearchChange(nextValue);
  };

  const handleSearchClear = () => {
    setInputValue("");
    debouncedOnSearchChange.cancel();
    onSearchChange?.("");
  };

  return (
    <>
      <div className="metadoc-table-panel">
        {onSearchChange && (
          <div className="metadoc-table-search">
            <span className="material-icons metadoc-table-search__icon">
              search
            </span>
            <input
              aria-label="Search Meta Documents"
              className="metadoc-table-search__input"
              onChange={handleSearchChange}
              placeholder={searchPlaceholder}
              value={inputValue}
            />
            {inputValue && (
              <button
                aria-label="Clear MetaDoc search"
                className="metadoc-table-search__clear"
                onClick={handleSearchClear}
                type="button"
              >
                <span className="material-icons">close</span>
              </button>
            )}
          </div>
        )}
        <Table
          className="documents-table metadoc-table"
          drawer={isDrawer}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          columns={columns as any}
          emptyTableMessage={emptyTableMessage}
          rows={documents}
          rowKeyGetter={(row) =>
            row.isChild
              ? `child-${row.parentMetadocId}-${row.id}`
              : `metadoc-${row.id}`
          }
          isLoading={isLoading}
          fullHeight={!isDrawer}
          pagination={
            onPageChange
              ? {
                  pageSize: PAGE_SIZE,
                  pageIndex: page,
                  rowCount: totalCount,
                  onPageChange,
                  isLoading,
                }
              : undefined
          }
          zebraStripes={true}
          customScrollbars={true}
        />
      </div>
      {/* table positioning interferes with tooltips, so we add it here with an id */}
      <Tooltip
        id="metadoc-open-tooltip"
        className="custom-tooltip"
        place="top"
      />
      <Tooltip
        id="metadoc-clone-tooltip"
        className="custom-tooltip"
        place="top"
      />
      <Tooltip
        id="metadoc-delete-tooltip"
        className="custom-tooltip"
        place="top"
      />
      <Tooltip
        id="document-name-tooltip"
        className="custom-tooltip"
        place="top"
      />
    </>
  );
};

export default MetaDocTable;
